import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from './AuthContext';
import './AuthPage.scss';
import { useNavigate } from 'react-router-dom';

const SignUpPage: React.FC = () => {
  const { signUp } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTOS, setAgreeToTOS] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<string[]>([]);
  const navigate = useNavigate();

  const validatePassword = (password: string): string[] => {
    const requirements = [
      { regex: /.{8,}/, message: "At least 8 characters long" },
      { regex: /[A-Z]/, message: "Contains an uppercase letter" },
      { regex: /[a-z]/, message: "Contains a lowercase letter" },
      { regex: /[0-9]/, message: "Contains a number" },
      { regex: /[^A-Za-z0-9]/, message: "Contains a special character" }
    ];

    return requirements
      .filter(requirement => !requirement.regex.test(password))
      .map(requirement => requirement.message);
  };

  useEffect(() => {
    setPasswordStrength(validatePassword(password));
  }, [password]);

  const handleSignUp = async () => {
    if (!email || !password || !confirmPassword) {
      setError('Please fill out all fields.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (passwordStrength.length > 0) {
      setError('Password does not meet all requirements.');
      return;
    }

    if (!agreeToTOS) {
      setError('You must agree to the Terms of Service and Privacy Policy.');
      return;
    }

    try {
      await signUp("", password, email);
      navigate('/confirm-email', { state: { email } });
    } catch (err) {
      setError('Failed to sign up. Please check your credentials.');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h2>Sign Up</h2>
        
        {error && <p className="error-message">{error}</p>}
        <div className="form-group">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
          />
        </div>
        <div className="form-group">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
          />
          {password && (
            <ul className="password-requirements">
              {['At least 8 characters long', 'Contains an uppercase letter', 'Contains a lowercase letter', 'Contains a number', 'Contains a special character'].map((req, index) => (
                <li key={index} className={passwordStrength.includes(req) ? 'unmet' : 'met'}>
                  {req}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="form-group">
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm Password"
          />
        </div>
        <div className="form-group">
          <input
            type="checkbox"
            id="agree-tos"
            checked={agreeToTOS}
            onChange={(e) => setAgreeToTOS(e.target.checked)}
          />
          <label htmlFor="agree-tos">
            I agree to the <a href="/terms" target="_blank">Terms of Service</a> and 
            <a href="/privacy" target="_blank"> Privacy Policy</a>.
          </label>
        </div>
        <button className="cta-button" onClick={handleSignUp}>
          Sign Up
        </button>
        <div className="additional-links">
          <a href="/login">Already have an account? Sign In</a>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;